import { IHome } from "@src/components/Home";
import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import ClientOnly from "@src/components/ClientOnly";
import { motion, AnimatePresence } from "framer-motion";
import Floater from "../Floater";
import Footer from "../Footer";
import Vendors from "../Vendors";
import Parts from "../Parts";
import FAQ from "../FAQ";
import Recommendations from "../Recommendations";
import Backstory from "../Backstory";
import Masonry from "../Masonry";
import Hero from "../Hero";
import AsoEbi from "../AsoEbiLink";
import Welcome from "../Welcome";
import { useEventParts } from "@src/hooks/useEventParts";
import { useHome } from "@src/hooks/useHome";
import WeddingParty from "../WeddingParty";
import {
  AsoebiStatusEnum,
  Event_PreferencesQuery,
  Event_PreferencesQueryVariables,
} from "@src/@types/api.d";
import { GetEventPreferences } from "@src/graphql/queries";
import styles from "./styles.module.scss";

interface ComponentItem {
  id: string;
  label: string;
  component: React.ReactNode;
}

const HomeT20: React.FC<IHome> = ({
  slug,
  eventBySlugData,
  eventId,
  eventPartsData,
  backStoryData,
  eventVendorData,
  albumDetailsData,
  heroImageData,
  hasWallet,
  getEventStreamersData,
  liveSchedules,
  eventUuid,
  weddingPartyData,
  faqData,
  recommendationData,
  inviteResult: initialInviteResult,
}) => {
  const [viewMode, setViewMode] = useState<"hero" | "components">("hero");
  const [activeComponentIndex, setActiveComponentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right"
  );

  const {
    accessToken,
    inviteResult,
    getAsoebiData,
    isCashGiftSetup,
    isInvitedToEvent,
  } = useHome({
    slug,
    eventBySlugData,
    eventId,
    eventPartsData,
    backStoryData,
    eventVendorData,
    albumDetailsData,
    heroImageData,
    hasWallet,
    getEventStreamersData,
    liveSchedules,
    eventUuid,
    weddingPartyData,
    faqData,
    recommendationData,
    inviteResult: initialInviteResult,
  });

  const { data: eventPreferencesData } = useQuery<
    Event_PreferencesQuery,
    Event_PreferencesQueryVariables
  >(GetEventPreferences, {
    variables: { eventId: eventUuid as string },
    skip: !eventUuid,
  });

  const shouldShowFaq = eventPreferencesData?.event_preferences?.faq ?? false;
  const shouldShowRecommendations =
    eventPreferencesData?.event_preferences?.recommendations ?? false;
  const { parts, hasEventParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone: eventBySlugData?.eventBySlug?.eventTimezone,
    isInvitedToEvent,
  });
  const hasActiveAsoebiGroup =
    (getAsoebiData?.getAseobi ?? []).some(
      (group) =>
        Boolean(group?.name?.trim()) &&
        group?.status === AsoebiStatusEnum.AsoebiStatusActive
    ) ?? false;

  // Build available components array
  const availableComponents = useMemo<ComponentItem[]>(() => {
    const components: ComponentItem[] = [];

    // Welcome — always first
    components.push({
      id: "welcome",
      label: "Welcome",
      component: (
        <Welcome
          slugData={eventBySlugData}
          heroImageData={heroImageData}
          albumDetailsData={albumDetailsData}
        />
      ),
    });

    // Backstory
    if ((backStoryData?.getBackStory?.length ?? 0) > 0) {
      components.push({
        id: "backstory",
        label: "Backstory",
        component: (
          <Backstory eventId={eventId} backStoryData={backStoryData} />
        ),
      });
    }

    // Parts (Events)
    if (hasEventParts) {
      components.push({
        id: "parts",
        label: "Events",
        component: (
          <Parts
            liveSchedules={liveSchedules}
            eventId={eventId}
            parts={parts}
            eventPartsData={eventPartsData}
            eventTimezone={eventBySlugData?.eventBySlug?.eventTimezone}
            albumDetailsData={albumDetailsData}
          />
        ),
      });
    }

    // FAQ
    if (shouldShowFaq && (faqData?.getFAQsByEvent?.data?.length ?? 0) > 0) {
      components.push({
        id: "faq",
        label: "FAQ",
        component: faqData ? (
          <FAQ
            eventId={eventId}
            faqData={faqData}
            albumDetailsData={albumDetailsData}
          />
        ) : null,
      });
    }

    // Recommendations
    if (
      shouldShowRecommendations &&
      (recommendationData?.getEventRecommendationLists?.length ?? 0) > 0
    ) {
      components.push({
        id: "recommendations",
        label: "Recommendations",
        component: (
          <Recommendations
            eventId={eventId}
            recommendationData={
              recommendationData as NonNullable<typeof recommendationData>
            }
          />
        ),
      });
    }

    // Photobook
    if ((albumDetailsData?.getMedia?.length ?? 0) > 0) {
      components.push({
        id: "masonry",
        label: "Photobook",
        component: (
          <Masonry
            eventSlug={slug}
            eventId={eventId}
            albumDetailsData={albumDetailsData}
          />
        ),
      });
    }

    // WeddingParty
    if ((weddingPartyData?.getWeddingParty?.length ?? 0) > 0) {
      components.push({
        id: "weddingparty",
        label: "Wedding Party",
        component: (
          <WeddingParty eventId={eventId} weddingPartyData={weddingPartyData} />
        ),
      });
    }

    // AsoEbi
    if (accessToken && hasActiveAsoebiGroup) {
      components.push({
        id: "asoebi",
        label: "Asoebi",
        component: (
          <AsoEbi
            eventUuid={eventUuid}
            eventId={eventId}
            showAsoebi={accessToken ? true : false}
            showCashGift={!!accessToken && isCashGiftSetup}
            isCashGiftSetup={isCashGiftSetup}
            availableGiftPaymentCurrencies={
              eventBySlugData?.eventBySlug?.available_gift_payment_currencies ??
              []
            }
          />
        ),
      });
    }

    // Vendors
    if ((eventVendorData?.eventVendor?.length ?? 0) > 0) {
      components.push({
        id: "vendors",
        label: "Vendors",
        component: (
          <Vendors eventId={eventId} eventVendorData={eventVendorData} />
        ),
      });
    }

    return components;
  }, [
    eventBySlugData,
    heroImageData,
    albumDetailsData,
    backStoryData,
    eventId,
    hasEventParts,
    liveSchedules,
    parts,
    shouldShowFaq,
    faqData,
    shouldShowRecommendations,
    recommendationData,
    hasActiveAsoebiGroup,
    weddingPartyData,
    eventVendorData,
    accessToken,
    getAsoebiData,
    slug,
    eventUuid,
    isCashGiftSetup,
  ]);

  const handleHeroNext = () => {
    setActiveComponentIndex(0);
    setViewMode("components");
    setSlideDirection("right");
  };

  const handleTabClick = (index: number) => {
    setSlideDirection(index > activeComponentIndex ? "right" : "left");
    setActiveComponentIndex(index);
  };

  const handleCoverTabClick = () => {
    setViewMode("hero");
    setSlideDirection("left");
  };

  const currentComponent = availableComponents[activeComponentIndex];

  const heroVariants = {
    initial: { opacity: 0, y: 50 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    exit: { opacity: 0, x: -100, transition: { duration: 0.4 } },
  };

  const componentsVariants = {
    initial: { opacity: 0, x: 100 },
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const componentExitVariants = {
    exit: {
      opacity: 0,
      x: slideDirection === "left" ? 100 : -100,
      transition: { duration: 0.3 },
    },
  };

  const componentEnterVariants = {
    initial: {
      opacity: 0,
      x: slideDirection === "left" ? -100 : 100,
    },
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const tabVariants = {
    idle: { scale: 1 },
    active: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95 },
  };

  return (
    <>
      <ClientOnly>
        <Floater
          accessToken={accessToken}
          eventId={eventId}
          eventUuid={eventUuid}
          slugData={eventBySlugData}
          hasWallet={hasWallet}
          getEventStreamersData={getEventStreamersData}
          liveSchedules={liveSchedules}
          access_control={
            eventBySlugData?.eventBySlug?.event_preference?.access_control ??
            false
          }
          qr_code={inviteResult?.signupByInviteLink?.invitation?.qr_code ?? ""}
          getAsoebiData={getAsoebiData}
          admits={inviteResult?.signupByInviteLink?.invitation?.admits ?? 1}
          invitation={inviteResult?.signupByInviteLink?.invitation}
          isInvitedToEvent={isInvitedToEvent}
        />

        <AnimatePresence>
          {viewMode === "hero" ? (
            <motion.div
              key="hero-view"
              variants={heroVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className={styles["hero-container"]}
            >
              <Hero
                heroImageData={heroImageData}
                eventId={eventId}
                slugData={eventBySlugData}
                openInvitationModal={
                  availableComponents.length > 0 ? handleHeroNext : undefined
                }
              />
            </motion.div>
          ) : (
            <motion.div
              key="components-view"
              variants={componentsVariants}
              initial="initial"
              animate="animate"
              className={styles["components-container"]}
            >
              {/* Header with tabs */}
              <motion.div
                className={styles["components-header"]}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
              >
                {/* Event name row */}
                {(eventBySlugData?.eventBySlug?.name ||
                  eventBySlugData?.eventBySlug?.hash_tag) && (
                  <div className={styles["event-title-row"]}>
                    {eventBySlugData?.eventBySlug?.name && (
                      <span className={styles["event-name"]}>
                        {eventBySlugData.eventBySlug.name}
                      </span>
                    )}
                    {eventBySlugData?.eventBySlug?.hash_tag && (
                      <span className={styles["event-hashtag"]}>
                        {eventBySlugData.eventBySlug.hash_tag}
                      </span>
                    )}
                  </div>
                )}
                <div className={styles["tabs-nav-row"]}>
                  <div className={styles["tabs-wrapper"]}>
                    <div className={styles["tabs-scroll"]}>
                      <motion.button
                        key="cover"
                        onClick={handleCoverTabClick}
                        className={styles["tab"]}
                        variants={tabVariants}
                        initial="idle"
                        animate="idle"
                        whileTap="tap"
                      >
                        <span className={styles["tab-label"]}>Cover</span>
                      </motion.button>

                      {availableComponents.map((component, index) => (
                        <motion.button
                          key={component.id}
                          onClick={() => handleTabClick(index)}
                          className={`${styles["tab"]} ${
                            activeComponentIndex === index
                              ? styles["active"]
                              : ""
                          }`}
                          variants={tabVariants}
                          initial="idle"
                          animate={
                            activeComponentIndex === index ? "active" : "idle"
                          }
                          whileTap="tap"
                        >
                          <span className={styles["tab-label"]}>
                            {component.label}
                          </span>
                          {activeComponentIndex === index && (
                            <motion.div
                              className={styles["tab-indicator"]}
                              layoutId="tab-indicator"
                              transition={{
                                type: "spring",
                                stiffness: 300,
                                damping: 30,
                              }}
                            />
                          )}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Active component */}
              <AnimatePresence>
                {currentComponent && (
                  <motion.div
                    key={currentComponent.id}
                    variants={componentEnterVariants}
                    initial="initial"
                    animate="animate"
                    exit={componentExitVariants.exit}
                    className={styles["component-wrapper"]}
                  >
                    {currentComponent.component}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer */}
              <Footer slug={eventBySlugData} />
            </motion.div>
          )}
        </AnimatePresence>
      </ClientOnly>
    </>
  );
};

export default HomeT20;
